import React from 'react';
import styled, { css } from 'styled-components';
import { motion } from 'framer-motion';
import { FaArrowRight, FaExternalLinkAlt, FaGithub, FaStar } from 'react-icons/fa';
import ProjectMedia from './ProjectMedia';
import useReveal from '../../utils/useReveal';
import { demoLabel, projectName } from '../../utils/projectHelpers';

const tagColors = ['var(--accent)', 'var(--green)', 'var(--blue)', 'var(--purple)', 'var(--accent)'];

const Card = styled.article`
  overflow: hidden;
  border-radius: 18px;
  background: var(--surface);
  border: 1px solid var(--border);
  box-shadow: var(--shadow-card);
  cursor: default;
  height: 100%;
  display: flex;
  flex-direction: column;
  transition: transform 180ms ease, box-shadow 180ms ease, border-color 180ms ease;

  &:hover {
    transform: translateY(-3px);
    border-color: rgba(249, 115, 22, 0.45);
    box-shadow: var(--shadow-card);
  }

  ${({ $hero }) => $hero && css`
    display: grid;
    grid-template-columns: minmax(330px, 0.78fr) minmax(0, 1.22fr);
    min-height: 330px;
    border-radius: 18px;

    @media (max-width: 900px) {
      grid-template-columns: 1fr;
    }
  `}
`;

const HeroInfo = styled.div`
  padding: 34px 30px;
  display: flex;
  flex-direction: column;
  justify-content: center;
  min-width: 0;

  @media (max-width: 560px) {
    padding: 24px 20px;
  }
`;

const Badge = styled.div`
  width: fit-content;
  display: inline-flex;
  align-items: center;
  gap: 8px;
  padding: 8px 12px;
  margin-bottom: 14px;
  border-radius: 9px;
  color: var(--accent);
  background: var(--orange-bg);
  font-size: 13px;
  font-weight: 600;
`;

const Title = styled.h3`
  margin: 0;
  color: var(--text);
  font-family: 'Instrument Sans', sans-serif;
  font-size: ${({ $hero }) => $hero ? 'clamp(34px, 4vw, 44px)' : '22px'};
  line-height: 1.25;
  letter-spacing: -0.035em;
  font-weight: 650;
`;

const Subtitle = styled.div`
  margin-top: 8px;
  color: var(--muted);
  font-size: ${({ $hero }) => $hero ? '16px' : '14px'};
  font-weight: 700;
`;

const Description = styled.p`
  margin: 16px 0 0;
  color: var(--muted);
  font-size: ${({ $hero }) => $hero ? '16px' : '14px'};
  line-height: 1.55;
  font-weight: 500;
  display: -webkit-box;
  -webkit-line-clamp: ${({ $hero }) => $hero ? 3 : 3};
  -webkit-box-orient: vertical;
  overflow: hidden;
`;

const Tags = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 9px;
  margin-top: ${({ $hero }) => $hero ? '18px' : '14px'};
`;

const Tag = styled.span`
  display: inline-flex;
  align-items: center;
  gap: 7px;
  min-height: 26px;
  padding: 5px 10px;
  border-radius: 999px;
  color: var(--text);
  background: var(--surface);
  border: 1px solid var(--border);
  font-size: 12px;
  font-weight: 600;

  &::before {
    content: '';
    width: 9px;
    height: 9px;
    border-radius: 50%;
    background: ${({ $color }) => $color};
  }
`;

const Actions = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 14px;
  margin-top: 22px;
`;

const PrimaryAction = styled.button`
  min-width: 158px;
  height: 44px;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  gap: 12px;
  border: 1px solid var(--text);
  border-radius: 13px;
  color: var(--button-text);
  background: var(--button-bg);
  font-size: 14px;
  font-weight: 650;
  cursor: pointer;
  transition: transform 160ms ease, box-shadow 160ms ease;

  &:hover {
    transform: translateY(-2px);
    box-shadow: 0 18px 34px rgba(17, 24, 39, 0.18);
  }
`;

const SecondaryAction = styled.button`
  min-width: 154px;
  height: 44px;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  gap: 12px;
  border: 1px solid var(--border);
  border-radius: 13px;
  color: ${({ $disabled }) => $disabled ? 'var(--muted)' : 'var(--text)'};
  background: var(--surface);
  font-size: 14px;
  font-weight: 650;
  cursor: ${({ $disabled }) => $disabled ? 'not-allowed' : 'pointer'};
  opacity: ${({ $disabled }) => $disabled ? 0.58 : 1};
  transition: transform 160ms ease, border-color 160ms ease;

  &:hover {
    transform: ${({ $disabled }) => $disabled ? 'none' : 'translateY(-2px)'};
    border-color: ${({ $disabled }) => $disabled ? 'var(--border)' : 'var(--accent)'};
  }
`;

const HeroMedia = styled.div`
  min-height: 300px;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 22px 32px 22px 0;
  background: radial-gradient(circle at 60% 42%, rgba(14, 165, 233, 0.2), transparent 45%);

  @media (max-width: 900px) {
    padding: 0 20px 24px;
  }
`;


const MiniBody = styled.div`
  padding: 22px;
  display: flex;
  flex-direction: column;
  flex: 1;
`;

const Category = styled.div`
  width: fit-content;
  padding: 5px 10px;
  margin-bottom: 9px;
  border-radius: 8px;
  color: var(--accent);
  background: var(--orange-bg);
  font-size: 12px;
  font-weight: 650;
`;

const MiniFooter = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
  margin-top: auto;
  padding-top: 16px;
  margin-top: 22px;
  border-top: 1px solid var(--border);
`;

const TextButton = styled.button`
  display: inline-flex;
  align-items: center;
  gap: 10px;
  color: var(--text);
  border: none;
  background: transparent;
  padding: 0;
  min-height: 44px;
  text-decoration: none;
  font-size: 14px;
  font-weight: 650;
  cursor: pointer;

  svg {
    color: var(--accent);
  }
`;

const GithubButton = styled.a`
  width: 44px;
  height: 44px;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  border-radius: 50%;
  color: var(--text);
  background: var(--surface);
  border: 1px solid var(--border);
  cursor: pointer;
`;

const sanitizeSubtitle = (project) => {
  if (project.isFeatured) return project.category === 'Featured' ? 'Featured Engineering Project' : project.category;
  return project.category || 'Project';
};

const ProjectCards = ({ project, setOpenModal, variant, index = 0 }) => {
  const isHero = variant === 'hero';
  const reveal = useReveal((index % 3) * 0.05);
  const tech = Array.isArray(project.technologies) ? project.technologies.slice(0, isHero ? 5 : 3) : [];

  const openDetails = (event) => {
    event.stopPropagation();
    setOpenModal({ state: true, project });
  };

  const name = projectName(project);
  const demo = demoLabel(project.webAppLink);

  if (isHero) {
    return (
      <Card
        as={motion.article}
        $hero
        {...reveal}
      >
        <HeroInfo>
          <Badge><FaStar /> {project.isFeatured ? 'Featured Project' : 'Project'}</Badge>
          <Title $hero>{name}</Title>
          <Subtitle $hero>{sanitizeSubtitle(project)}</Subtitle>
          <Description $hero>{project.description || 'No description available.'}</Description>
          <Tags $hero>
            {tech.map((item, index) => (
              <Tag key={item} $color={tagColors[index % tagColors.length]}>{item}</Tag>
            ))}
          </Tags>
          <Actions>
            <PrimaryAction type="button" onClick={openDetails}>View Details <FaArrowRight /></PrimaryAction>
            <SecondaryAction as="a" href={project.githubLink} target="_blank" rel="noopener noreferrer"><FaGithub /> Repository</SecondaryAction>
            {demo && <SecondaryAction as="a" href={project.webAppLink} target="_blank" rel="noopener noreferrer">{demo} <FaExternalLinkAlt /></SecondaryAction>}
          </Actions>
        </HeroInfo>
        <HeroMedia>
          <ProjectMedia project={project} large />
        </HeroMedia>
      </Card>
    );
  }

  return (
    <Card
      as={motion.article}
      {...reveal}
    >
      <ProjectMedia project={project} />
      <MiniBody>
        <Category>{project.status || project.category || 'Project'}</Category>
        <Title>{name}</Title>
        <Description>{project.description || 'No description available.'}</Description>
        <Tags>
          {tech.map((item, index) => (
            <Tag key={item} $color={tagColors[index % tagColors.length]}>{item}</Tag>
          ))}
        </Tags>
        <MiniFooter>
          <TextButton type="button" onClick={openDetails} aria-label={`Read ${name} case study`}>Case Study <FaArrowRight /></TextButton>
          {project.githubLink && <GithubButton href={project.githubLink} target="_blank" rel="noopener noreferrer" aria-label={`${name} GitHub repository`}><FaGithub /></GithubButton>}
        </MiniFooter>
        {demo && <TextButton as="a" style={{ marginTop: 16 }} href={project.webAppLink} target="_blank" rel="noopener noreferrer">{demo} <FaExternalLinkAlt /></TextButton>}
      </MiniBody>
    </Card>
  );
};

export default ProjectCards;

